import Request from "../../../services/request";
import { SchemeNode } from "@/helpers/constructors";

export default {
  namespaced: true,
  state: {
    tree: [],
    loading: false,
    entry: new SchemeNode(),
    deleteModal: false,
  },
  mutations: {
    setTree(state, array) {
      state.tree = Array.isArray(array) ? array : [];
    },
    setEntry(state, object) {
      state.entry = object ? object : new SchemeNode();
    },
    clearEntry(state) {
      state.entry = new SchemeNode();
    },
    setPropertyEntry(state, { key, payload }) {
      state.entry[key] = payload;
    },
    changeLoading(state, value) {
      state.loading = value;
    },
    deleteModalCommit(state, value) {
      state.deleteModal = value;
    },
  },
  actions: {
    stateModalAction({ commit }, value) {
      commit("deleteModalCommit", value);
    },
    async getTree({ commit }) {
      commit("changeLoading", true);

      const query = Request.modifyQuery({}, ["*", "_file"]);
      try {
        const response = await Request.get(
          this.state.BASE_URL + `/dictionary/scheme-nodes/tree${query}`
        );
        commit("setTree", response.data);
        return response;
      } catch (e) {
        console.log(e);
        commit("change_notification_modal", e, { root: true });
      } finally {
        commit("changeLoading", false);
      }
    },
    async createEntry({ commit, dispatch }, node) {
      commit("changeLoading", true);
      try {
        const response = await Request.post(
          this.state.BASE_URL + "/dictionary/scheme-nodes",
          node
        );
        await dispatch("getTree");
        return response;
      } catch (e) {
        console.log(e);
        commit("change_notification_modal", e, { root: true });
      } finally {
        commit("changeLoading", false);
      }
    },
    async updateEntry({ commit, dispatch }, node) {
      commit("changeLoading", true);
      try {
        const response = await Request.put(
          this.state.BASE_URL + "/dictionary/scheme-nodes/" + node.id,
          node
        );
        await dispatch("getTree");
        return response;
      } catch (e) {
        console.log(e);
        commit("change_notification_modal", e, { root: true });
      } finally {
        commit("changeLoading", false);
      }
    },
    async deleteEntry({ commit, dispatch }, id) {
      commit("changeLoading", true);
      commit("deleteModalCommit", false);
      try {
        const response = await Request.delete(
          this.state.BASE_URL + "/dictionary/scheme-nodes/" + id
        );
        await dispatch("getTree");
        return response;
      } catch (e) {
        console.log(e);
        commit("change_notification_modal", e, { root: true });
      } finally {
        commit("changeLoading", false);
      }
    },
    async uploadSvgForNode({ commit, dispatch }, { node, file }) {
      commit("changeLoading", true);
      try {
        const uploadResponse = await dispatch(
          "uploadFileGeneral",
          { uuid: crypto.randomUUID(), file, category: "scheme-nodes" },
          { root: true }
        );
        if (!uploadResponse || uploadResponse.isError || !uploadResponse.data) {
          return uploadResponse;
        }

        // anchors не трогаем здесь — бэк сам разберётся: если это первая
        // загрузка SVG узлу, посчитает автоматически (SchemeNodeAnchorsDetector);
        // если файл уже был, оставит как есть (в т.ч. ручные удаления лишних
        // точек), пересчёт по новому файлу — отдельной кнопкой «Пересчитать».
        await Request.put(
          this.state.BASE_URL + "/dictionary/scheme-nodes/" + node.id,
          {
            ...node,
            id_file: uploadResponse.data.id,
          }
        );
        await dispatch("getTree");
        return uploadResponse;
      } catch (e) {
        console.log(e);
        commit("change_notification_modal", e, { root: true });
      } finally {
        commit("changeLoading", false);
      }
    },
    // Без changeLoading — используется для тихой подгрузки viewBox под пикер
    // точек крепления, общий спиннер диалога тут не нужен
    async getSvgViewbox({ commit }, id) {
      try {
        const query = Request.modifyQuery({}, ["_svg_viewbox"]);
        const response = await Request.get(
          this.state.BASE_URL + `/dictionary/scheme-nodes/${id}${query}`
        );
        // show() отдаёт объект (getOnly().first()), не массив
        return response.data ? response.data._svg_viewbox : null;
      } catch (e) {
        console.log(e);
        commit("change_notification_modal", e, { root: true });
        return null;
      }
    },
    // Без changeLoading — тихая подгрузка элементов легенды (краны, клапаны
    // и т.д.), обнаруженных на SVG узла, под подсветку/клик в редакторе. Вместе с ними — варианты
    // сборки узла (_detected_variants: [1] или [1, 2, 3] по подпапкам «2», «3» в Elements).
    async getDetectedElements({ commit }, id) {
      try {
        const query = Request.modifyQuery({}, ["_detected_elements", "_detected_variants"]);
        const response = await Request.get(
          this.state.BASE_URL + `/dictionary/scheme-nodes/${id}${query}`
        );
        const data = response.data || {};
        return {
          elements: data._detected_elements || [],
          variants: data._detected_variants && data._detected_variants.length ? data._detected_variants : [1],
        };
      } catch (e) {
        console.log(e);
        commit("change_notification_modal", e, { root: true });
        return { elements: [], variants: [1] };
      }
    },
    // Трубы справочника, найденные в SVG узла (_detected_pipes), и свежий разбор точек с кодом
    // трубы (_detected_anchors) — для таблицы «трубы × стороны». Без changeLoading — тихая
    // подгрузка рядом с viewBox.
    async getDetectedPipes({ commit }, id) {
      try {
        const query = Request.modifyQuery({}, ["_detected_pipes", "_detected_anchors"]);
        const response = await Request.get(
          this.state.BASE_URL + `/dictionary/scheme-nodes/${id}${query}`
        );
        const data = response.data || {};
        return {
          pipes: data._detected_pipes || [],
          anchors: data._detected_anchors || null,
        };
      } catch (e) {
        console.log(e);
        commit("change_notification_modal", e, { root: true });
        return { pipes: [], anchors: null };
      }
    },
    // В каких модульных схемах размещён узел: { schemes: [...], frozen_slots }. Без changeLoading —
    // тихая подгрузка в карточку узла; null при ошибке, чтобы блок просто не показывался.
    async getUsage({ commit }, id) {
      try {
        const response = await Request.get(
          this.state.BASE_URL + `/dictionary/scheme-nodes/${id}/usage`
        );
        // Ошибочный HTTP-ответ тоже проходит через Request как Logging;
        // например, без токена API возвращает data: []. Не передавать такой
        // ответ в шаблон как usage: там ожидается объект с массивом schemes.
        const usage = response && response.data;
        return usage && Array.isArray(usage.schemes) ? usage : null;
      } catch (e) {
        console.log(e);
        commit("change_notification_modal", e, { root: true });
        return null;
      }
    },
    async getFunctionalities({ commit }, id) {
      try {
        const response = await Request.get(
          this.state.BASE_URL + `/dictionary/scheme-nodes/${id}/functionalities`
        );
        return response && !response.isError && Array.isArray(response.data)
          ? response.data
          : [];
      } catch (e) {
        console.log(e);
        commit("change_notification_modal", e, { root: true });
        return [];
      }
    },
    async saveFunctionality({ commit }, { idNode, functionality }) {
      try {
        const url = this.state.BASE_URL + `/dictionary/scheme-nodes/${idNode}/functionalities`;
        const payload = {
          text: functionality.text,
          position: functionality.position || 0,
          element_ids: functionality.element_ids || [],
          element_refs: functionality.element_refs || [],
        };
        const response = functionality.id
          ? await Request.put(`${url}/${functionality.id}`, payload)
          : await Request.post(url, payload);
        if (!response || response.isError || !response.data) {
          commit(
            "change_notification_modal",
            { message: (response && response.message) || "Не удалось сохранить функциональность", error: true },
            { root: true }
          );
          return null;
        }
        return response.data;
      } catch (e) {
        console.log(e);
        commit("change_notification_modal", e, { root: true });
        return null;
      }
    },
    async deleteFunctionality({ commit }, { idNode, id }) {
      try {
        const response = await Request.delete(
          this.state.BASE_URL + `/dictionary/scheme-nodes/${idNode}/functionalities/${id}`
        );
        if (!response || response.isError) {
          commit(
            "change_notification_modal",
            { message: (response && response.message) || "Не удалось удалить функциональность", error: true },
            { root: true }
          );
          return false;
        }
        return true;
      } catch (e) {
        console.log(e);
        commit("change_notification_modal", e, { root: true });
        return false;
      }
    },
    async detectAnchorsForNode({ commit }, id) {
      try {
        return await Request.post(
          this.state.BASE_URL + `/dictionary/scheme-nodes/${id}/detect-anchors`,
          {}
        );
      } catch (e) {
        console.log(e);
        commit("change_notification_modal", e, { root: true });
        return null;
      }
    },
  },
};
